import { useEffect } from "react";

/**
 * Lightweight per-route meta tag manager. Replaces title + description +
 * og:* + twitter:* + canonical on the fly so that crawlers that DO render
 * JS (Googlebot, Bingbot) see the right metadata for each route. The
 * defaults baked into `index.html` cover the homepage; other routes call
 * this hook with their own copy.
 *
 * We avoided react-helmet-async to keep the bundle small and to skip the
 * extra Provider boilerplate. Direct DOM mutation works fine for our needs:
 * the SPA already has full ownership of the document, and these mutations
 * idempotently restore on unmount so route changes don't bleed metadata.
 *
 * Usage:
 *     useMeta({
 *         title: "Mi recomendación · RecomendaLibros",
 *         description: "5 libros para los que disfrutaron de Cien años…",
 *         canonical: "https://recomendalibros.com/r/abc123",
 *         image: "https://recomendalibros.com/api/og/r/abc123.jpg",
 *     });
 */
const SITE = "https://recomendalibros.com";

function setMeta(selector, attr, value) {
    if (!value) return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        // Selector pattern is `meta[name="..."]` or `meta[property="..."]`.
        // Extract the attribute name + value to recreate the missing tag.
        const m = selector.match(/meta\[(name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
}

function setLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export default function useMeta({ title, description, canonical, image, type = "website" } = {}) {
    useEffect(() => {
        const previousTitle = document.title;
        const previousDesc = document.head.querySelector('meta[name="description"]')?.getAttribute("content");
        const previousCanonical = document.head.querySelector('link[rel="canonical"]')?.getAttribute("href");
        const previousOgImage = document.head.querySelector('meta[property="og:image"]')?.getAttribute("content");

        if (title) {
            document.title = title;
            setMeta('meta[property="og:title"]', "content", title);
            setMeta('meta[name="twitter:title"]', "content", title);
        }
        if (description) {
            setMeta('meta[name="description"]', "content", description);
            setMeta('meta[property="og:description"]', "content", description);
            setMeta('meta[name="twitter:description"]', "content", description);
        }
        if (canonical) {
            const fullCanonical = canonical.startsWith("http") ? canonical : SITE + canonical;
            setLink("canonical", fullCanonical);
            setMeta('meta[property="og:url"]', "content", fullCanonical);
        }
        if (image) {
            const fullImage = image.startsWith("http") ? image : SITE + image;
            setMeta('meta[property="og:image"]', "content", fullImage);
            setMeta('meta[name="twitter:image"]', "content", fullImage);
        }
        if (type) {
            setMeta('meta[property="og:type"]', "content", type);
        }

        // On unmount, restore whatever was there before so the next route
        // doesn't inherit stale metadata.
        return () => {
            document.title = previousTitle;
            if (previousDesc !== undefined && previousDesc !== null) {
                setMeta('meta[name="description"]', "content", previousDesc);
                setMeta('meta[property="og:description"]', "content", previousDesc);
                setMeta('meta[name="twitter:description"]', "content", previousDesc);
            }
            if (previousCanonical) setLink("canonical", previousCanonical);
            if (previousOgImage) {
                setMeta('meta[property="og:image"]', "content", previousOgImage);
                setMeta('meta[name="twitter:image"]', "content", previousOgImage);
            }
        };
    }, [title, description, canonical, image, type]);
}
